import { useIntl } from 'react-intl';
import { Prompt } from 'sonata-react-components';

import './PauseConfirmationPrompt.style.scss';

export type PauseConfirmationPromptProps = {
  campaignPlanName: string;
  entityType: 'lineItem' | 'io';
  ioName?: string;
  isLoading?: boolean;
  isOpen: boolean;
  lineItemName?: string;
  onCancel: () => void;
  onClose?: () => void;
  onConfirm: () => void;
};

export default function PauseConfirmationPrompt({
  campaignPlanName,
  entityType,
  ioName,
  isLoading = false,
  isOpen,
  lineItemName,
  onCancel,
  onClose,
  onConfirm
}: Readonly<PauseConfirmationPromptProps>) {
  const { formatMessage } = useIntl();

  if (!isOpen) {
    return null;
  }

  const isLineItem = entityType === 'lineItem';

  const title = isLineItem
    ? formatMessage({
        defaultMessage: 'You are going to pause an active Line Item',
        id: 'pauseConfirmation.lineItem.title'
      })
    : formatMessage({
        defaultMessage: 'You are going to pause an active IO',
        id: 'pauseConfirmation.io.title'
      });

  const confirmLabel = isLineItem
    ? formatMessage({
        defaultMessage: 'Pause Line Item',
        id: 'pauseConfirmation.lineItem.confirm'
      })
    : formatMessage({
        defaultMessage: 'Pause IO',
        id: 'pauseConfirmation.io.confirm'
      });

  const cancelLabel = formatMessage({
    defaultMessage: 'Cancel',
    id: 'common.cancel'
  });

  const confirmationText = formatMessage({
    defaultMessage: 'Are you sure you want to proceed?',
    id: 'pauseConfirmation.confirmationText'
  });

  const handleClose = (ev?: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    ev?.stopPropagation();
    (onClose ?? onCancel)();
  };

  return (
    <Prompt
      buttons={[
        {
          disabled: isLoading,
          id: 'pause-confirmation-cancel',
          loading: false,
          onClick: onCancel,
          size: 'small',
          text: cancelLabel,
          type: 'secondary'
        },
        {
          disabled: isLoading,
          id: 'pause-confirmation-confirm',
          loading: isLoading,
          onClick: onConfirm,
          size: 'small',
          text: confirmLabel,
          type: 'primary'
        }
      ]}
      closePrompt={handleClose}
      title={title}
    >
      <div className="pauseConfirmationPrompt">
        <div className="pauseConfirmationPrompt__card">
          {isLineItem ? (
            <>
              <div className="pauseConfirmationPrompt__cardRow">
                <span className="pauseConfirmationPrompt__cardLabelTitle">
                  {formatMessage({
                    defaultMessage: 'Line Item:',
                    id: 'pauseConfirmation.lineItem.label'
                  })}
                </span>
                <span className="pauseConfirmationPrompt__cardValueEntity">{lineItemName}</span>
              </div>
              <div className="pauseConfirmationPrompt__cardRow">
                <span className="pauseConfirmationPrompt__cardLabel">
                  {formatMessage({
                    defaultMessage: 'Campaign plan:',
                    id: 'pauseConfirmation.campaignPlan.label'
                  })}
                </span>
                <span className="pauseConfirmationPrompt__cardValue">{campaignPlanName}</span>
                <span className="pauseConfirmationPrompt__cardLabel">{'/'}</span>
                <span className="pauseConfirmationPrompt__cardLabel">
                  {formatMessage({
                    defaultMessage: 'IO:',
                    id: 'pauseConfirmation.io.label'
                  })}
                </span>
                <span className="pauseConfirmationPrompt__cardValue">{ioName ? `${ioName}` : ''}</span>
              </div>
            </>
          ) : (
            <>
              <div className="pauseConfirmationPrompt__cardRow">
                <span className="pauseConfirmationPrompt__cardLabelTitle">
                  {formatMessage({
                    defaultMessage: 'IO:',
                    id: 'pauseConfirmation.io.label'
                  })}
                </span>
                <span className="pauseConfirmationPrompt__cardValueEntity">{ioName}</span>
              </div>
              <div className="pauseConfirmationPrompt__cardRow">
                <span className="pauseConfirmationPrompt__cardLabel">
                  {formatMessage({
                    defaultMessage: 'Campaign plan:',
                    id: 'pauseConfirmation.campaignPlan.label'
                  })}
                </span>
                <span className="pauseConfirmationPrompt__cardValue">{campaignPlanName}</span>
              </div>
            </>
          )}
        </div>
        <p className="pauseConfirmationPrompt__confirmationText">{confirmationText}</p>
      </div>
    </Prompt>
  );
}
